"use client";

import cn from "@utils/cn";
import Image from "next/image";
import { FC } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { FeatureListProps } from "@/types";

import "./FeatureList.css";

SwiperCore.use([Pagination, Navigation]);

const FeatureList: FC<FeatureListProps> = ({ entry }) => {
  const isSlider = entry.variant === "slider";

  return (
    <div className="bg-gray-7 py-l md:py-xxl px-m md:px-0 overflow-hidden">
      <div
        className={cn(
          "md:w-22\\/24 md:mx-auto rounded-2xl py-[40px] px-m md:px-[40px] text-center",
          entry.backgroundToggle && "bg-white  shadow-extra-strong",
        )}
      >
        {entry.title && <h2 className="font-primary text-l uppercase">{entry.title}</h2>}
        {entry.text && <h2 className="font-secondary font-secondary-light text-[14px] mt-l">{entry.text}</h2>}
        {entry.cta && !isSlider && (
          <Button
            href={entry.cta.link.url}
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.BLACK}
            type="button"
            variant={ButtonVariant.PRIMARY}
            customClass="inline-flex mt-l"
          >
            {entry.cta.label}
          </Button>
        )}
        {!isSlider && (
          <div className="flex flex-wrap justify-center gap-xs md:gap-l mt-[40px]">
            {entry.baseElements?.map((item, i) => {
              const Tag = item.cardLink?.link.url ? "a" : "div";
              return (
                <Tag
                  key={i}
                  {...(item.cardLink && { href: item.cardLink.link.url })}
                  className={cn(
                    "bg-gray-7 rounded-lg h-[160px] w-[calc(50%-4px)] md:w-[calc(20%-24px)]  flex flex-col items-center p-xs md:p-m",
                    entry.shadowToggle && " shadow-strong",
                  )}
                >
                  <div className="flex flex-col items-center h-full justify-between">
                    {item.image && (
                      <div className="h-[64px] flex items-center justify-center">
                        <Image className="object-contain" src={item.image.url} alt={item.image.title || ""} width={64} height={64} />
                      </div>
                    )}

                    {item.title && <div className="font-primary text-s uppercase text-center leading-tight">{item.title}</div>}
                  </div>
                </Tag>
              );
            })}
          </div>
        )}
      </div>
      {isSlider && (
        <>
          <div className="relative w-screen left-1/2 -translate-x-1/2 overflow-hidden mt-[40px]">
            <Swiper
              navigation={{
                nextEl: `.swiper-button-next-${entry.id}-featurelist`,
                prevEl: `.swiper-button-prev-${entry.id}-featurelist`,
              }}
              pagination={{ type: "progressbar", el: `.swiper-pagination-carousel-${entry.id}-featurelist` }}
              className="feature-list-slider"
              spaceBetween={16}
              slidesPerView={2.2}
              slidesOffsetBefore={16}
              slidesOffsetAfter={16}
              watchSlidesProgress={true}
              breakpoints={{
                768: { slidesPerView: 4.2, spaceBetween: 24, slidesOffsetBefore: 40, slidesOffsetAfter: 40 },
                1024: { slidesPerView: 6.2, spaceBetween: 24, slidesOffsetBefore: 40, slidesOffsetAfter: 40 },
              }}
            >
              {entry.baseElements?.map((item, i) => {
                const Tag = item.cardLink?.link.url ? "a" : "div";
                return (
                  <SwiperSlide key={i} className="!h-auto">
                    <Tag {...(item.cardLink && { href: item.cardLink.link.url })} className="flex flex-col items-center h-full px-xs">
                      {item.image && (
                        <div className="h-[40px] flex items-center justify-center mb-xs">
                          <Image className="object-contain" src={item.image.url} alt={item.image.title || ""} width={40} height={40} />
                        </div>
                      )}
                      {item.title && <div className="font-primary text-xl uppercase leading-tight">{item.title}</div>}
                      {item.subtitle && (
                        <div className="font-secondary font-secondary-light text-s mt-xs leading-tight text-center">{item.subtitle}</div>
                      )}
                    </Tag>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          <div className="flex flex-col items-center gap-l mt-l">
            {entry.baseElements?.length > 2 && (
              <div className="flex items-center justify-center gap-m w-full max-w-[280px]">
                <button
                  type="button"
                  aria-label="Previous slide"
                  className={`feature-list-nav-btn swiper-button-prev-${entry.id}-featurelist flex items-center justify-center w-[44px] h-[44px] rounded-full border border-gray-10 shrink-0 stroke-black disabled:opacity-30`}
                >
                  <Icon name="arrow-left" size="20" title="Previous slide" />
                </button>
                <div
                  className={`swiper-pagination-carousel swiper-pagination-carousel-${entry.id}-featurelist relative w-full h-1 bg-gray-10 rounded-full overflow-hidden`}
                />
                <button
                  type="button"
                  aria-label="Next slide"
                  className={`feature-list-nav-btn swiper-button-next-${entry.id}-featurelist flex items-center justify-center w-[44px] h-[44px] rounded-full border border-gray-10 shrink-0 stroke-black disabled:opacity-30`}
                >
                  <Icon name="arrow-right" size="20" title="Next slide" />
                </button>
              </div>
            )}
            {entry.cta && (
              <Button
                href={entry.cta.link.url}
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.PRIMARY}
                customClass="inline-flex"
              >
                {entry.cta.label}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FeatureList;
